import { Button } from '@/components/ui/button.tsx';
import { useState } from 'react';
import { usePostBegin2FASetup } from '@/features/auth/hooks/mutations/use-post-begin-2fa-setup.ts';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Copy, Check, RectangleEllipsis } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import QRCode from 'qrcode';
import { StatusAlert } from '../status-alert';

const loader = document.querySelector('.loader-container')!;
const showLoader = () => {
  loader.classList.remove('loader--hide');
};

export function TwoFactorSetupForm() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [beginSetup2FA, setBeginSetup2FA] = useState<boolean>(false);
  const [secret, setSecret] = useState<{
    manualCode: string;
    qrCodeUrl: string;
    qrCodeDataUrl: string;
  } | null>(null);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const { setIsAuthenticated, isAuthenticated } = useAuthStore();

  const {
    mutate: begin2FASetupMutation,
    isPending,
    isError,
  } = usePostBegin2FASetup({
    onSuccess: async (data) => {
      // The "Redundant 'await' for a non-promise type" is not true
      // The QRCode.toDataURL returns a promise
      const qrCodeDataUrl = await QRCode.toDataURL(data.qrCodeUrl);
      setSecret({
        ...data,
        qrCodeDataUrl,
      });
      setStatusMessage(null);
    },
    onError: () => {
      setStatusMessage({
        type: 'error',
        message: 'Failed to set up two-factor authentication. Please try again.',
      });
    },
  });

  function onSkipForNow() {
    showLoader();

    setIsAuthenticated(true);
    navigate('/', { replace: true });
  }

  function onBegin2FA() {
    setStatusMessage(null);
    setBeginSetup2FA(true);
    begin2FASetupMutation();
  }

  function onRetrySetup() {
    setStatusMessage(null);
    setSecret(null);
    begin2FASetupMutation();
  }

  function onContinueVerificationCode() {
    navigate('/verification-2fa', {
      replace: true,
      state: {
        from: '2FA_SETUP_PAGE',
      },
    });
  }

  if (state?.from !== 'LOGIN_PAGE') {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  if (!beginSetup2FA) {
    return <TwoFactorSetupInitial onSkipForNow={onSkipForNow} onBegin2FA={onBegin2FA} />;
  }

  if (isError) {
    return (
      <TwoFactorSetupError
        errorMessage={statusMessage?.message || 'Oops, something went wrong.'}
        onRetry={onRetrySetup}
        onSkipForNow={onSkipForNow}
      />
    );
  }

  if (isPending) {
    return <TwoFactorSetupPending />;
  }

  if (secret) {
    return (
      <TwoFactorSetupSuccess
        secret={secret}
        onContinueVerificationCode={onContinueVerificationCode}
      />
    );
  }

  return <TwoFactorSetupInitial onSkipForNow={onSkipForNow} onBegin2FA={onBegin2FA} />;
}

function CopyToClipboard({
  text,
  onCopy,
  className = '',
  notificationText = 'Copied to clipboard!',
  notificationDuration = 2000,
}: {
  text: string;
  onCopy?: () => void;
  className?: string;
  notificationText?: string;
  notificationDuration?: number;
}) {
  const [copied, setCopied] = useState<boolean>(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text ?? '');
    setCopied(true);
    onCopy?.();

    setTimeout(() => {
      setCopied(false);
    }, notificationDuration);
  }

  return (
    <div className={`relative ${className}`}>
      <Button
        onClick={handleCopy}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 hover:bg-gray-200"
        title={copied ? notificationText : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Copy className="h-4 w-4 text-gray-600" />
        )}
      </Button>

      {copied && (
        <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 transform">
          <div className="rounded-md bg-gray-900 px-3 py-1.5 text-xs whitespace-nowrap text-white shadow-lg">
            {notificationText}
          </div>
        </div>
      )}
    </div>
  );
}

export function TwoFactorSetupPending() {
  return (
    <div className="mx-auto max-w-md">
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <LoadingSpinner className="text-primary size-14" />
        <p className="text-md text-gray-600">Setting up 2FA...</p>
      </div>
    </div>
  );
}

export function TwoFactorSetupInitial({
  onSkipForNow,
  onBegin2FA,
}: {
  onSkipForNow: () => void;
  onBegin2FA: () => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <div className="flex min-h-[200px] flex-col justify-between gap-4 sm:min-h-[150px]">
        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <RectangleEllipsis size="52" className="text-[#383E42]" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Enable Two-Factor Authentication
          </h2>
          <p className="text-sm leading-relaxed text-gray-600">
            Add an extra layer of security to your account by enabling 2FA
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            className="text-md h-10 flex-1 border-0 bg-gray-100 font-medium text-gray-700 transition-colors hover:bg-gray-200"
            variant="ghost"
            onClick={onSkipForNow}
          >
            Skip for now
          </Button>
          <Button
            className="text-md h-10 flex-1 font-medium text-white shadow-sm transition-all hover:shadow-md"
            onClick={onBegin2FA}
          >
            Enable 2FA
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TwoFactorSetupSuccess({
  secret,
  onContinueVerificationCode,
}: {
  secret: {
    manualCode: string;
    qrCodeUrl: string;
    qrCodeDataUrl: string;
  };
  onContinueVerificationCode: () => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <div className="space-y-4">
        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-semibold text-gray-900">Set Up Authenticator</h2>
          <p className="text-sm text-gray-600">Scan the QR code with your authenticator app</p>
        </div>

        <div className="flex justify-center">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <img src={secret.qrCodeDataUrl} alt="2FA QR Code" className="h-48 w-48" />
          </div>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-500">Or enter code manually</span>
            </div>
          </div>

          <div className="w-full items-center rounded-lg bg-gray-50 px-4 py-2">
            <div className="flex items-center justify-between">
              <code className="overflow-hidden font-mono text-sm text-gray-900 select-all">
                {secret.manualCode}
              </code>
              <CopyToClipboard
                text={secret.manualCode}
                className="ml-2"
                notificationText="Secret code copied!"
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button
            onClick={onContinueVerificationCode}
            className="text-md h-10 w-full font-medium text-white shadow-sm transition-all hover:shadow-md"
          >
            Continue to Verification
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TwoFactorSetupError({
  errorMessage,
  onRetry,
  onSkipForNow,
}: {
  errorMessage: string;
  onRetry: () => void;
  onSkipForNow: () => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <div className="flex max-w-[400px] flex-col gap-4">
        <StatusAlert type="error" message={errorMessage} />

        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <RectangleEllipsis size="52" className="text-[#383E42]" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Two-Factor Authentication Failed
          </h2>
          <p className="text-sm text-gray-600">
            We couldn't set up two-factor authentication. Please try again.
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            className="text-md h-10 flex-1 border-0 bg-gray-100 font-medium text-gray-700 transition-colors hover:bg-gray-200"
            variant="ghost"
            onClick={onSkipForNow}
          >
            Skip for now
          </Button>
          <Button
            className="text-md h-10 flex-1 font-medium text-white shadow-sm transition-all hover:shadow-md"
            onClick={onRetry}
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}
